import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useIssueCard, useRequestPhysicalCard } from '../../hooks/useCards';
import type { CardType, CardBrand, Address } from '../../types/cards';

export function RequestCard() {
  const navigate = useNavigate();
  const issueCard = useIssueCard();
  const requestPhysical = useRequestPhysicalCard();

  const [step, setStep] = useState(1);
  const [cardType, setCardType] = useState<CardType>('PREPAID');
  const [isPhysical, setIsPhysical] = useState(false);
  const [brand, setBrand] = useState<CardBrand>('VISA');
  const [address, setAddress] = useState<Address>({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Brasil',
  });

  const handleSubmit = async () => {
    if (isPhysical) {
      await requestPhysical.mutateAsync({
        type: cardType,
        brand,
        deliveryAddress: address,
      });
    } else {
      await issueCard.mutateAsync({
        type: cardType,
        brand,
      });
    }
    navigate('/banking/cards');
  };

  const isLoading = issueCard.isPending || requestPhysical.isPending;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Solicitar Cartão</h1>
        <p className="text-gray-500">Escolha o tipo de cartão que deseja</p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 rounded ${
              s <= step ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Tipo de Cartão</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`p-4 border-2 rounded-lg cursor-pointer ${
                cardType === 'PREPAID' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
              }`}
              onClick={() => setCardType('PREPAID')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Cartão Pré-pago</h3>
                  <p className="text-sm text-gray-500">
                    Use o saldo da sua conta virtual para compras
                  </p>
                </div>
                <Badge variant="success">Recomendado</Badge>
              </div>
            </div>

            <div
              className={`p-4 border-2 rounded-lg cursor-pointer ${
                cardType === 'CREDIT' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
              }`}
              onClick={() => setCardType('CREDIT')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Cartão de Crédito</h3>
                  <p className="text-sm text-gray-500">
                    Compre agora e pague na fatura
                  </p>
                </div>
                <Badge variant="primary">Sujeito a análise</Badge>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)}>Continuar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Formato e Bandeira</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Formato</label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer ${
                    !isPhysical ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setIsPhysical(false)}
                >
                  <h3 className="font-medium">Virtual</h3>
                  <p className="text-sm text-gray-500">Emissão imediata</p>
                  <p className="text-sm text-green-600 mt-2">Grátis</p>
                </div>
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer ${
                    isPhysical ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setIsPhysical(true)}
                >
                  <h3 className="font-medium">Físico</h3>
                  <p className="text-sm text-gray-500">Entrega em até 10 dias</p>
                  <p className="text-sm text-gray-600 mt-2">R$ 25,00</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Bandeira</label>
              <div className="grid grid-cols-3 gap-4">
                {(['VISA', 'MASTERCARD', 'ELO'] as CardBrand[]).map((b) => (
                  <div
                    key={b}
                    className={`p-4 border-2 rounded-lg cursor-pointer text-center ${
                      brand === b ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => setBrand(b)}
                  >
                    <span className="font-medium">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep(1)}>
                Voltar
              </Button>
              <Button onClick={() => setStep(isPhysical ? 3 : 4)}>
                {isPhysical ? 'Continuar' : 'Confirmar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && isPhysical && (
        <Card>
          <CardHeader>
            <CardTitle>Endereço de Entrega</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">CEP</label>
                <Input
                  value={address.zipCode}
                  onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                  placeholder="00000-000"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Rua</label>
                <Input
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Número</label>
                <Input
                  value={address.number}
                  onChange={(e) => setAddress({ ...address, number: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Complemento</label>
                <Input
                  value={address.complement}
                  onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Bairro</label>
                <Input
                  value={address.neighborhood}
                  onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cidade</label>
                <Input
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Estado</label>
                <Input
                  value={address.state}
                  onChange={(e) => setAddress({ ...address, state: e.target.value })}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep(2)}>
                Voltar
              </Button>
              <Button onClick={() => setStep(4)}>Confirmar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Confirmar Solicitação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Tipo</span>
                <span className="font-medium">
                  {cardType === 'PREPAID' ? 'Pré-pago' : 'Crédito'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Formato</span>
                <span className="font-medium">{isPhysical ? 'Físico' : 'Virtual'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bandeira</span>
                <span className="font-medium">{brand}</span>
              </div>
              {isPhysical && (
                <>
                  <hr />
                  <div>
                    <span className="text-gray-500">Endereço de Entrega</span>
                    <p className="font-medium">
                      {address.street}, {address.number}
                      {address.complement && ` - ${address.complement}`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {address.neighborhood}, {address.city} - {address.state}
                    </p>
                    <p className="text-sm text-gray-500">{address.zipCode}</p>
                  </div>
                  <hr />
                  <div className="flex justify-between">
                    <span className="text-gray-500">Taxa de Emissão</span>
                    <span className="font-medium">R$ 25,00</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep(isPhysical ? 3 : 2)}>
                Voltar
              </Button>
              <Button onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Processando...' : 'Solicitar Cartão'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default RequestCard;
